import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import type { FlexProps } from "@chakra-ui/react";
import { Flex, FormLabel, Icon, Input, Text, useMultiStyleConfig } from "@chakra-ui/react";
import parse from "html-react-parser";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import { BsCircle } from "react-icons/bs";
import { FlexColumn } from "@/components/FlexColumn/FlexColumn";
import { FlexRow } from "@/components/FlexRow/FlexRow";
import { WithTooltip } from "@/components/WithTooltip/WithTooltip";
import Button from "@/components/chakra/Button";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { formatHypothetical, htmlify } from "@/services/utils";
import { AccessibleFormControl } from "../AccessibleFormControl/AccessibleFormControl";
import { AccessibleSelect } from "../AccessibleSelect/AccessibleSelect";

export interface InvestmentFigureUnitSelectionProps {
	currencyLabel: string;
	unitType: string;
	unitValue: string;
	isOpen: boolean;
	description?: string;
	percentageLabel?: string;
	tooltip?: string;
	investmentValueLabel?: string;
	saveLabel?: string;
	baseCurrencySymbol?: string;
	onToggleOpen: () => void;
	onUnitTypeChange: (value: string) => void;
	onUnitValueChange: (value: string) => void;
	onSave: () => void;
	unitTypeLabel: string;
}

export type InvestmentFigureUnitSelectionServerProps = Omit<
	InvestmentFigureUnitSelectionProps,
	"onUnitTypeChange" | "onUnitValueChange" | "onToggleOpen" | "onSave" | "isOpen" | "unitType" | "unitValue"
>;

const MODAL_WIDTH = "324px";

export const InvestmentFigureUnitSelection = ({
	currencyLabel,
	isOpen,
	onToggleOpen,
	unitType,
	unitValue,
	onUnitTypeChange,
	onUnitValueChange,
	description,
	percentageLabel,
	tooltip,
	investmentValueLabel,
	baseCurrencySymbol = "",
	unitTypeLabel,
	saveLabel
}: InvestmentFigureUnitSelectionProps) => {
	const blockInvalidChar = (e: KeyboardEvent<HTMLInputElement>) => {
		return ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();
	};
	const styleParts = useMultiStyleConfig("InvestmentFigureUnitSelection", {
		variant: isOpen ? "visiblyOpen" : ""
	});

	const instanceConfig = useInstanceConfig();

	const [currentUnitType, setCurrentUnitType] = useState("percentage");
	const [currentUnitValue, setCurrentUnitValue] = useState<string>();

	const handleUnitValueChange = (evt: ChangeEvent<HTMLInputElement>) => {
		setCurrentUnitValue(evt.target.value.slice(0, 10));
	};

	const handleUnitTypeChange = (evt: ChangeEvent<HTMLSelectElement>) => {
		setCurrentUnitType(evt.target.value);
	};

	useEffect(() => {
		setCurrentUnitType(unitType);
		setCurrentUnitValue(unitValue);
	}, [unitType, unitValue]);

	const formatValue = useMemo(() => {
		if (!currentUnitValue) {
			return "";
		}

		return htmlify(
			formatHypothetical({
				value: 100,
				includeInvestmentAmount: false,
				unitType: currentUnitType,
				unitValue: parseFloat(currentUnitValue),
				unitCurrency: "",
				format: { type: "number", decimalPlaces: 0 },
				instanceConfig: instanceConfig,
				abbreviate: false
			})
		);
	}, [currentUnitValue, currentUnitType, instanceConfig]);

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			const target = event.target as Node;
			const container = document.getElementById("investmentFigureUnitSelection");
			if (container && !container.contains(target) && isOpen) {
				onToggleOpen();
			}
		},
		[isOpen, onToggleOpen]
	);

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [handleClickOutside]);

	// Handle Escape key to close the dropdown
	useEffect(() => {
		const handleKeyDown = (event: globalThis.KeyboardEvent) => {
			if (event.key === "Escape" && isOpen) {
				event.preventDefault();
				onToggleOpen();
			}
		};
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onToggleOpen]);

	const toggleButtonRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	// Move focus to the panel when it opens
	useEffect(() => {
		if (isOpen && panelRef.current) {
			// Focus the first input inside the panel
			const firstFocusableElement = panelRef.current.querySelector<HTMLElement>(
				"select, input, button, [tabindex]:not([tabindex='-1'])"
			);
			firstFocusableElement?.focus();
		}
	}, [isOpen]);

	const onHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "ArrowDown" && !isOpen) {
			event.preventDefault();
			onToggleOpen();
		}
	};

	const handleSave = () => {
		if (!currentUnitType || !currentUnitValue) {
			return;
		}
		onUnitTypeChange(currentUnitType);
		onUnitValueChange(currentUnitValue);
		onToggleOpen();
	};

	return (
		<FlexColumn
			width={{ base: "auto", md: MODAL_WIDTH }}
			data-testid="investmentFigureUnitSelection"
			id="investmentFigureUnitSelection"
			{...(styleParts.root as FlexProps)}
		>
			<Flex
				flexDirection="column"
				{...(styleParts.header as FlexProps)}
				as="button"
				onClick={onToggleOpen}
				aria-expanded={isOpen}
				aria-controls="investmentFigureUnitSelectionPanel"
				role="button"
				ref={toggleButtonRef}
				onKeyDown={onHeaderKeyDown}
			>
				<FlexRow
					width="full"
					justifyContent="space-between"
					alignItems="center"
					flex={1}
					paddingX={4}
					gap={{ base: 2, md: 3 }}
					cursor="pointer"
				>
					<FlexRow display={{ base: "none", md: "flex" }} gap={4}>
						<Flex position="relative" width={{ base: 30, md: 38 }} justifyContent="center" alignItems="center">
							<Icon as={BsCircle} fontSize={38} position="absolute" />
							<Text fontSize={15}>{currentUnitType == "percentage" ? "%" : parse(baseCurrencySymbol)}</Text>
						</Flex>
						<FlexColumn flex={1} gap={1} alignItems="start">
							<Text variant="smallLabel" wordBreak="break-word" whiteSpace="initial">
								{htmlify(description)}:
							</Text>
							<Text variant="largeLabel" fontWeight="bold" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
								{currentUnitType === "percentage" ? htmlify(percentageLabel) : `${htmlify(currencyLabel)} ${formatValue}`}
							</Text>
						</FlexColumn>
					</FlexRow>
					<Flex maxWidth={{ base: "20px", md: "36px" }}>
						<Icon as={isOpen ? BiChevronUp : BiChevronDown} fontSize={36} ml="auto" />
					</Flex>
				</FlexRow>
			</Flex>

			{/* Content Panel */}
			<Flex
				flexDirection="column"
				id="investmentFigureUnitSelectionPanel"
				role="region"
				aria-labelledby="investmentFigureUnitSelection"
				hidden={!isOpen}
				ref={panelRef}
				width={{ base: "100%", md: MODAL_WIDTH }}
				{...(styleParts.panel as FlexProps)}
			>
				<FlexColumn gap={1}>
					<Text variant="label" whiteSpace="break-spaces" overflowWrap="anywhere">
						{htmlify(description)}:
					</Text>
					<AccessibleSelect
						id="investment-unit-type"
						aria-label={unitTypeLabel ? unitTypeLabel : "Unit type"}
						value={currentUnitType}
						onChange={handleUnitTypeChange}
						label={unitTypeLabel ?? "Unit type"}
					>
						<option value="percentage" style={{ color: "black !important", backgroundColor: "white !important" }}>
							{htmlify(percentageLabel)}
						</option>
						<option value="currency" style={{ color: "black !important", backgroundColor: "white !important" }}>
							{htmlify(currencyLabel)}
						</option>
					</AccessibleSelect>
				</FlexColumn>
				<AccessibleFormControl
					id="investment-unit"
					flexDirection="column"
					gap={1}
					display={currentUnitType === "currency" ? "flex" : "none"}
				>
					<FormLabel>
						<WithTooltip customVariant="topIndex" tooltip={tooltip}>
							<Text fontSize="14px">{htmlify(investmentValueLabel)}:</Text>
						</WithTooltip>
					</FormLabel>
					<Input type="number" value={currentUnitValue} onChange={handleUnitValueChange} onKeyDown={blockInvalidChar} />
				</AccessibleFormControl>

				<Button type="button" variant="outline" onClick={handleSave}>
					{saveLabel}
				</Button>
			</Flex>
		</FlexColumn>
	);
};
